import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Pagination } from '@pmi/dsm-react/dist';
import { SortedColumn, HeaderPropSortCallback, FooterColumn } from './models';
import Header from './Header';
import styles from './table.module.scss';
import Footer from './Footer';

interface IPageSizes {
  label: string;
  value: string;
}

interface IPaginationProps {
  alignment?: string;
  customClassName?: string;
  totalPages?: number;
  showPageSizes?: boolean;
  disabled?: boolean;
  size?: number;
  pageSizes?: IPageSizes[];
  theme?: string;
  page?: number;
  pageSize?: number;
  onPageChange?: (pageNumber: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  globalPageSize?: boolean;
}

interface ResponsiveTableProps {
  data: any[];
  columns: SortedColumn[];
  onSort?: HeaderPropSortCallback;
  showPagination?: boolean;
  paginationProps?: IPaginationProps;
  footerColumns?: FooterColumn[];
  className?: string | null;
}

function buildColumns(
  columns: SortedColumn[],
  onSort: HeaderPropSortCallback,
): ColumnDef<any, any>[] {
  const columnHelper = createColumnHelper<any>();
  const columnDefs: ColumnDef<any, any>[] = [];

  columns.forEach((column) => {
    const def = columnHelper.accessor(column.name, {
      header: () => (
        <Header key={column.name} column={column} onSort={onSort} />
      ),
      cell: column.cell,
    });
    def.size = column.width;
    def.id = column.name;
    columnDefs.push(def);
  });

  return columnDefs;
}

function updateColumnSorting(
  columns: SortedColumn[],
  columnSorted: SortedColumn,
) {
  return columns.map((col) => ({
    ...col,
    direction: col.name === columnSorted.name ? col.direction : undefined,
  }));
}

export default function ResponsiveTable({
  data,
  columns,
  onSort = null,
  showPagination = false,
  paginationProps = {},
  footerColumns = [],
  className = null,
}: ResponsiveTableProps) {
  const [tableRows, setTableRows] = useState(data);

  const [tableColumns, setTableColumns] = useState(columns);

  const sortWrapper: HeaderPropSortCallback = (col) => {
    const newcols = updateColumnSorting(tableColumns, col);
    setTableColumns(newcols);
    if (onSort) onSort(col);
  };

  const {
    customClassName,
    totalPages = 10,
    showPageSizes = true,
    disabled = false,
    pageSizes = [
      {
        label: '10 per page',
        value: '10',
      },
      {
        label: '25 per page',
        value: '25',
      },
      {
        label: '50 per page',
        value: '50',
      },
      {
        label: '100 per page',
        value: '100',
      },
    ],
    page = 1,
    pageSize = 10,
    onPageChange,
    onPageSizeChange,
    globalPageSize = false,
  } = paginationProps;
  const columnDefs = useMemo(
    () => buildColumns(tableColumns, sortWrapper),
    [columns, tableColumns],
  );

  const table = useReactTable({
    data: tableRows,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    setTableRows(data);
  }, [data]);

  const onPageChangeDsmCompatible = (e: unknown, pageNumber: number) =>
    onPageChange(pageNumber);

  const localPageSize = Number(localStorage.getItem('pageSize') ?? 0);
  const onPageSizeChangeEvent = (updatedPageSize: number) => {
    if (onPageSizeChange && globalPageSize) {
      onPageSizeChange(updatedPageSize);
    }
    localStorage.setItem('pageSize', updatedPageSize.toString());
  };

  // IMP - do not remove below useEffect, else it will cause unknown issues in rendering table cells when there is an update
  // always generate columns in useMemo() with correct dependencies to prevent rendering loop
  useEffect(() => {
    setTableColumns(columns);
  }, [columns]);

  useEffect(() => {
    if (localPageSize > 0) {
      onPageSizeChangeEvent(localPageSize);
    }
  }, []);

  return (
    <>
      <div className={`${className || ''} d-block`}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup, i: number) => (
              <tr
                key={`${headerGroup.id}-${i.toString()}-${window.btoa(
                  Math.random().toString(),
                )}`}
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: `${header.column.columnDef.size}%` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={`Row${row.id}`}>
                {row.getVisibleCells().map((cell) => (
                  <td key={`Col${cell.id}`} aria-label={cell.column.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            {footerColumns?.length > 0 && (
              <Footer
                className={styles.footerRow}
                data={tableRows}
                footerColumns={footerColumns}
              />
            )}
          </tfoot>
        </table>
      </div>
      {/* Pagination */}
      {showPagination && (
        <div className={styles.paginationControl}>
          <Pagination
            data-testid="test-pagination"
            className={`${customClassName} justify-content-center`}
            totalPages={totalPages}
            showPageSizes={showPageSizes}
            disabled={disabled}
            pageSizes={pageSizes}
            onChange={onPageChangeDsmCompatible}
            onPageSizeChange={onPageSizeChangeEvent}
            currentPage={page}
            pageSize={pageSize}
          />
        </div>
      )}
    </>
  );
}
